import React from 'react';
import { Button } from '../Button/Button';

export interface DialogProps {
  /**
   * Tipo de diálogo (success, error, warning, info)
   */
  type?: 'success' | 'error' | 'warning' | 'info';
  /**
   * Título del diálogo
   */
  title: string;
  /**
   * Mensaje del diálogo
   */
  message?: string;
  /**
   * Contenido personalizado (alternativa a message)
   */
  children?: React.ReactNode;
  /**
   * Función al cerrar el diálogo
   */
  onClose: () => void;
  /**
   * Función al confirmar (opcional)
   */
  onConfirm?: () => void;
  /**
   * Texto del botón de confirmación
   */
  confirmText?: string;
  /**
   * Texto del botón de cancelación
   */
  cancelText?: string;
  /**
   * Si es true, muestra solo el botón de cerrar
   */
  singleAction?: boolean;
  /**
   * Icono personalizado (opcional)
   */
  customIcon?: React.ReactNode;
  /**
   * Estado de carga en el botón de confirmación
   */
  isConfirming?: boolean;
  /**
   * Texto que se muestra durante la confirmación
   */
  confirmingText?: string;
  /**
   * Ocultar el botón de confirmación
   */
  hideConfirm?: boolean;
}

export const Dialog: React.FC<DialogProps> = ({
  type = 'success',
  title,
  message,
  children,
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  singleAction = false,
  customIcon,
  isConfirming = false,
  confirmingText,
  hideConfirm = false,
}) => {
  const typeStyles = {
    success: {
      iconWrapper: 'bg-success-bg',
      icon: 'text-success',
    },
    error: {
      iconWrapper: 'bg-error-bg',
      icon: 'text-error',
    },
    warning: {
      iconWrapper: 'bg-warning-bg',
      icon: 'text-warning',
    },
    info: {
      iconWrapper: 'bg-info-bg',
      icon: 'text-info',
    },
  };

  const icons = {
    success: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    error: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    warning: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    info: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    // No cerramos automáticamente para permitir control manual después de async
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isConfirming) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center 
                 justify-center z-[2000] p-4"
      onClick={handleOverlayClick}
      style={{
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        className="bg-surface-1 rounded-lg p-6 max-w-[420px] w-full relative"
        style={{
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)',
          animation: 'scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Botón de cerrar */}
        <button type="button"
          onClick={onClose}
          disabled={isConfirming}
          className="absolute top-4 right-4 p-2 text-tertiary flex items-center justify-center 
                     transition-all duration-150 rounded-md hover:bg-surface-hover hover:text-primary 
                     active:scale-[0.92] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Icono y título */}
        <div className="flex items-center gap-4 mb-5">
          <div
            className={`w-12 h-12 flex-shrink-0 rounded-[10px] flex items-center justify-center ${typeStyles[type].iconWrapper}`}
          >
            <div className={typeStyles[type].icon}>
              {customIcon || icons[type]}
            </div>
          </div>

          <div className="flex-1 pr-8">
            <div className="text-[18px] font-semibold text-primary leading-[1.4]">{title}</div>
          </div>
        </div>

        {/* Mensaje o contenido personalizado */}
        <div className="text-body-sm text-tertiary leading-[1.6] mb-6 pl-16">
          {children || message}
        </div>

        {/* Botones */}
        {!singleAction && !hideConfirm && (
          <div className="flex gap-3 justify-end">
            <Button 
              variant="secondary" 
              size="md" 
              onClick={onClose}
              disabled={isConfirming}
            >
              {cancelText || 'Cancelar'}
            </Button>

            <Button
              variant="primary"
              size="md"
              onClick={handleConfirm}
              loading={isConfirming}
              loadingText={confirmingText || 'Procesando...'}
              disabled={isConfirming}
              className={type === 'error' ? '!bg-red-600 hover:!bg-red-700' : ''}
            >
              {confirmText || 'Confirmar'}
            </Button>
          </div>
        )}

        {(singleAction || hideConfirm) && (
          <div className="flex justify-end">
            <Button 
              variant="primary" 
              size="md" 
              onClick={onClose}
              disabled={isConfirming}
            >
              {confirmText || 'Aceptar'}
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            transform: scale(0.96) translateY(16px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};



